import { isVerifiedContact } from './gtm-verified-contact'
import type { GtmDemoRunResponse } from '@laplace/contracts'

/** The GTM employee's process, in order. Mirrors the Explee-style chain. */
export const GTM_RUN_STAGES = [
  'research',
  'competitors',
  'campaigns',
  'companies',
  'decision_makers',
  'emails',
] as const

export type GtmRunStageKey = (typeof GTM_RUN_STAGES)[number]

export type GtmRunStageStatus =
  | 'pending'
  | 'active'
  | 'done'
  | 'missing_contacts'
  | 'failed'

export interface GtmRunStageView {
  key: GtmRunStageKey
  status: GtmRunStageStatus
  /** Stage content is built on sample data (the fixture lead source). */
  sample: boolean
}

/**
 * Maps the backend run onto the six-step chain. Every step follows the run;
 * a step never finishes before the one above it.
 */
export function resolveGtmRunStages(
  run: GtmDemoRunResponse | undefined,
): Array<GtmRunStageView> {
  // The API hands out leads only with the finished run, so the backend being
  // past its lead step is not enough to show them.
  const leadsShown = run?.status === 'completed' || Boolean(run?.leads.length)

  const doneByStage: Record<GtmRunStageKey, boolean> = {
    research: Boolean(run?.profile),
    competitors: Boolean(run?.competitors),
    campaigns: Boolean(run?.campaigns),
    companies: Boolean(run?.companies),
    decision_makers: leadsShown,
    emails: run?.status === 'completed',
  }
  const hasVerifiedContact = Boolean(
    run?.leads.some((lead) => isVerifiedContact(lead.contact)),
  )

  let blocked = false
  let failureShown = false
  return GTM_RUN_STAGES.map((key) => {
    // Leads (and so emails) are synthetic until a lead provider is chosen.
    const sample =
      (key === 'decision_makers' || key === 'emails') &&
      Boolean(run?.sampleData)

    if (!blocked && doneByStage[key]) {
      if (key === 'emails' && !hasVerifiedContact) {
        return { key, status: 'missing_contacts', sample }
      }
      return { key, status: 'done', sample }
    }
    // The first stage that is not done is where the run is (or stopped).
    const wasBlocked = blocked
    blocked = true
    if (wasBlocked) return { key, status: 'pending', sample }
    if (run?.status === 'failed' && !failureShown) {
      failureShown = true
      return { key, status: 'failed', sample }
    }
    return { key, status: run ? 'active' : 'pending', sample }
  })
}

export function isGtmRunChainComplete(stages: Array<GtmRunStageView>): boolean {
  return stages.every((stage) => stage.status === 'done')
}

/**
 * The signed-in Analysis: research and campaign strategy only. Writing
 * emails (the demo's sixth stage) is outreach work and lives in Leads; the
 * stored six-stage run is unchanged.
 */
export const GTM_WORKSPACE_ANALYSIS_STAGE_COUNT = 5

export function resolveGtmWorkspaceAnalysisStages(
  run: GtmDemoRunResponse | undefined,
): Array<GtmRunStageView> {
  return resolveGtmRunStages(run).filter((stage) => stage.key !== 'emails')
}

/** The Analysis step (1-based) that shows the campaign strategy. */
export const GTM_CAMPAIGNS_ANALYSIS_STEP =
  GTM_RUN_STAGES.indexOf('campaigns') + 1
